import i18n from '@/i18n';
import { format, isValid, parseISO } from 'date-fns';

export function resolveGalleryItemPrompt(raw) {
  if (!raw) return '';
  const candidates = [
    raw.prompt,
    raw.aiGenerationPrompt,
    raw.aiPrompt,
    raw.generationPrompt,
    raw.description,
  ];
  for (const candidate of candidates) {
    const text = String(candidate || '').trim();
    if (text) return text;
  }
  return '';
}

function parseGalleryDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  try {
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const date = parseISO(normalized);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
}

export function resolveGalleryItemDate(item) {
  const candidates = [
    item?.createdAt,
    item?.uploadedAt,
    item?.updatedAt,
    item?.batchDate,
  ];
  for (const candidate of candidates) {
    const parsed = parseGalleryDate(candidate);
    if (parsed) return parsed;
  }
  return null;
}

/** 分组标题：完整日期 yyyy-MM-dd */
export function formatGalleryDayLabel(date) {
  if (!date || !isValid(date)) return i18n.t('workbench:ipeUtilError.unknownDate');
  return format(date, 'yyyy-MM-dd');
}

export function groupGalleryItemsByDate(items = []) {
  const groups = new Map();

  items.forEach((item) => {
    const date = resolveGalleryItemDate(item);
    const key = date ? format(date, 'yyyy-MM-dd') : 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        dateKey: key,
        date,
        label: formatGalleryDayLabel(date),
        items: [],
      });
    }
    groups.get(key).items.push(item);
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.dateKey === 'unknown') return 1;
    if (b.dateKey === 'unknown') return -1;
    return b.dateKey.localeCompare(a.dateKey);
  });
}

/** 按 yyyy-MM-dd 区间筛选（含起止日全天） */
export function filterItemsByDateRange(items = [], { startDate = '', endDate = '' } = {}) {
  const start = String(startDate || '').trim();
  const end = String(endDate || '').trim();
  if (!start && !end) return items;

  const startTime = start ? new Date(`${start}T00:00:00`).getTime() : null;
  const endTime = end ? new Date(`${end}T23:59:59`).getTime() : null;

  return (items || []).filter((item) => {
    const date = resolveGalleryItemDate(item);
    if (!date) return false;
    const time = date.getTime();
    if (startTime != null && endTime != null) return time >= startTime && time <= endTime;
    if (startTime != null) return time >= startTime;
    if (endTime != null) return time <= endTime;
    return true;
  });
}

export function normalizeGalleryItem(raw, tabId) {
  if (!raw) return null;
  const url = raw.url || raw.src || raw.dataUrl || raw.imageUrl || '';
  if (!url) return null;
  const prompt = resolveGalleryItemPrompt(raw);
  return {
    id: String(raw.id || `${tabId}-${url}`),
    url,
    thumbUrl: raw.thumbUrl || raw.referenceUrl
    || raw.previewUrl || null,
    title: raw.name || raw.title || prompt || '',
    prompt,
    width: raw.imageWidth || raw.width || null,
    height: raw.imageHeight || raw.height || null,
    createdAt: raw.createdAt || raw.uploadedAt || raw.updatedAt || raw.batchDate || null,
    source: raw.source || raw.sourceType || tabId,
    aiGenerated: Boolean(raw.aiGenerated),
    aiProcessType: raw.aiProcessType || raw.sourceType || null,
    category: raw.category || tabId,
  };
}
